import React, { useEffect, useState } from "react";
import "@/App.css";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollText, FlaskConical, BookOpen, ArrowLeft, Settings, Trophy, KeyRound, AlertTriangle, History } from "lucide-react";
import SpellOverlapPage from "@/pages/SpellOverlapPage";
import LoginPage from "@/pages/LoginPage";
import InvitePage, { inviteToken } from "@/pages/InvitePage";
import ResetPasswordPage, { resetToken } from "@/pages/ResetPasswordPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { UserMenu } from "@/components/UserMenu";
import { ModelCheckDetail } from "@/pages/ModelCheckHistory";
import { LlmSettingsDialog } from "@/components/LlmSettingsDialog";
import { useLlmSettings, openLlmSettings } from "@/lib/llmSettings";
import { NavProvider, useNav } from "@/lib/nav";
import { RulesPage } from "@/pages/RulesPage";
import { RulebookPage } from "@/pages/RulebookPage";
import { SteamrollerPage, SteamrollerSectionDetail } from "@/pages/SteamrollerPage";
import { ProposeWorkspace } from "@/pages/ProposePage";
import { HistoryPage, HistoryDetail } from "@/pages/HistoryPage";
import { AdminPage } from "@/pages/AdminPage";
import { RuleDetail } from "@/components/RuleComponents";
import { CardDetail } from "@/components/CardDetail";
import { RulebookSectionDetail } from "@/pages/RulebookPage";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";

function Header({ health }) {
  return (
    <header className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/morrhus-logo.png" alt="Morrstofa" className="h-9 w-auto" data-testid="app-logo" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Morrstofa</h1>
            <p className="text-xs text-muted-foreground -mt-0.5">
              Warmachine MK4 — rules writing & playtest workspace
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HealthBadge health={health} />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

function LlmButton() {
  const llm = useLlmSettings();
  const attached = Boolean(llm?.has_key);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={openLlmSettings}
      className={`h-7 gap-1.5 text-xs ${attached ? "text-cyan-400 border-cyan-700/50" : "text-rose-300 border-rose-700/60"}`}
      data-testid="llm-settings-btn"
    >
      <KeyRound className="w-3.5 h-3.5" />
      {attached ? llm.model : "No LLM attached"}
    </Button>
  );
}

function HealthBadge({ health }) {
  if (!health) return <Badge variant="outline" className="text-muted-foreground">connecting…</Badge>;
  if (health.status !== "ok")
    return <Badge variant="destructive">API offline</Badge>;
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="text-emerald-400 border-emerald-700/50">● live</Badge>
      <LlmButton />
    </div>
  );
}

function NoLlmBanner() {
  const llm = useLlmSettings();
  if (!llm || llm.has_key) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-rose-700/60 bg-rose-950/30 px-4 py-3 text-sm" data-testid="no-llm-banner">
      <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0" />
      <span className="flex-1 min-w-[240px]">
        <strong className="text-rose-200">No LLM attached.</strong>{" "}
        The AI features (rule analysis, conflict checks, revisions, packs) won't work until you add your own API key.
      </span>
      <Button size="sm" onClick={openLlmSettings} data-testid="no-llm-banner-btn">
        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Attach your LLM
      </Button>
    </div>
  );
}

function FooterLlm() {
  const llm = useLlmSettings();
  return (
    <span data-testid="footer-llm">
      LLM: {llm?.has_key ? `${llm.provider}/${llm.model}` : "none"}{" "}
      {llm?.has_key ? (
        <Badge variant="outline" className="ml-1 text-emerald-400 border-emerald-700/50">your key</Badge>
      ) : (
        <Badge variant="outline" className="ml-1 text-rose-300 border-rose-700/50">not attached</Badge>
      )}
    </span>
  );
}

function TabBar() {
  const { tab, switchTab } = useNav();
  const isAdmin = useAuth()?.user?.role === "admin";
  const tabs = [
    { id: "rules", label: "Special Rules", icon: ScrollText },
    { id: "rulebook", label: "Rulebook", icon: BookOpen },
    { id: "steamroller", label: "Steamroller", icon: Trophy },
    { id: "propose", label: "Writing & Checks", icon: FlaskConical },
    { id: "history", label: "History", icon: History },
    ...(isAdmin ? [{ id: "admin", label: "Admin", icon: Settings }] : []),
  ];
  return (
    <div className="inline-flex rounded-lg border border-border bg-card p-1 mb-5" data-testid="tab-bar">
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition ${
              active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-card/80"
            }`}
            data-testid={`tab-${t.id}`}
            data-active={active}
          >
            <Icon className="w-4 h-4" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function BackBar() {
  const { canGoBack, back } = useNav();
  if (!canGoBack) return null;
  return (
    <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={back} data-testid="back-btn">
      <ArrowLeft className="w-4 h-4 mr-1" /> Back
    </Button>
  );
}

function CurrentView() {
  const { view } = useNav();
  switch (view.type) {
    case "rules-root":
      return <RulesPage />;
    case "rule":
      return <RuleDetail ruleId={view.id} />;
    case "card":
      return <CardDetail cardId={view.id} />;
    case "spell-overlap":
      return <SpellOverlapPage />;
    case "model-check-detail":
      return <ModelCheckDetail id={view.id} />;
    case "rulebook-root":
      return <RulebookPage />;
    case "rulebook-section":
      return <RulebookSectionDetail id={view.id} />;
    case "steamroller-root":
      return <SteamrollerPage />;
    case "steamroller-section":
      return <SteamrollerSectionDetail id={view.id} />;
    case "propose":
      return null;
    case "history":
      return <HistoryPage />;
    case "history-detail":
      return <HistoryDetail id={view.id} />;
    case "admin-root":
      return <AdminPage />;
    default:
      return <p>Unknown view: {view.type}</p>;
  }
}

// Propose Rule stays mounted after the first visit so fields and results survive tab switches.
function ProposeKeepAlive() {
  const { view } = useNav();
  const active = view.type === "propose";
  const [visited, setVisited] = useState(active);
  useEffect(() => { if (active) setVisited(true); }, [active]);
  if (!visited) return null;
  return (
    <div className={active ? "" : "hidden"} data-testid="propose-keepalive">
      <ProposeWorkspace />
    </div>
  );
}

function AppShell() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth({ status: "error" }));
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header health={health} />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <NoLlmBanner />
        <TabBar />
        <BackBar />
        <CurrentView />
        <ProposeKeepAlive />
      </main>
      <footer className="max-w-7xl mx-auto px-4 py-6 text-xs text-muted-foreground">
        {health?.snapshot && (
          <div className="flex flex-wrap gap-4">
            <span>Snapshot {health.snapshot.version_id}</span>
            <span>{health.snapshot.counts?.cards ?? "?"} cards</span>
            <span>{health.counts?.rules ?? "?"} unique rules</span>
            <span>{health.counts?.rulebook_sections ?? "?"} rulebook sections</span>
            <FooterLlm />
          </div>
        )}
      </footer>
      <LlmSettingsDialog />
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm" data-testid="auth-checking">Loading…</div>;
  if (user === false) {
    if (resetToken()) return <ResetPasswordPage token={resetToken()} />;
    const token = inviteToken();
    return token ? <InvitePage token={token} /> : <LoginPage />;
  }
  return (
    <NavProvider>
      <AppShell />
    </NavProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Gate />
      <Toaster theme="dark" />
    </AuthProvider>
  );
}

export default App;
